import React from 'react';
import Select, { components, createFilter, StylesConfig, OptionProps, ValueContainerProps, MultiValueProps } from 'react-select';

export interface MultiSelectOption {
    value: string | number;
    label: string;
}

const SELECT_ALL_VALUE = '__select_all__';

interface MultiSelectDropdownProps {
    options: MultiSelectOption[];
    value: MultiSelectOption[];
    onChange: (selected: MultiSelectOption[]) => void;
    placeholder?: string;
    isSearchable?: boolean;
    isDisabled?: boolean;
    isClearable?: boolean;
    name?: string;
    enableSelectAll?: boolean;
    selectAllLabel?: string;
    allSelectedLabel?: string;
}

const optionFilter = createFilter<MultiSelectOption>();

const areAllSelected = (options: MultiSelectOption[], selected: MultiSelectOption[]) => {
    if (options.length === 0) return false;
    const selectedValues = new Set(selected.map((item) => String(item.value)));
    return options.every((option) => selectedValues.has(String(option.value)));
};

const customStyles: StylesConfig<MultiSelectOption, true> = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        borderRadius: 4,
        // border: state.isFocused ? '1.5px solid #6366F1' : '1px solid #E5E7EB',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(99,102,241,0.15)' : 'none',
        padding: '2px 6px',
        cursor: 'text',
        // '&:hover': {
        //     border: state.isFocused ? '1.5px solid #6366F1' : '1px solid #D1D5DB',
        // },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 4px', gap: 4, position: 'relative', flexWrap: 'nowrap', overflow: 'hidden', }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 15, color: '#111827', flex: '1 1 auto', minWidth: '50px' }),
    placeholder: (base) => ({ ...base, fontSize: 15, color: '#9CA3AF' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: '#9CA3AF',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 150ms ease',
        cursor: 'default',
    }),
    menu: (base) => ({
        ...base,
        marginTop: 6,
        borderRadius: 4,
        border: '1px solid #F3F4F6',
        boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
        overflow: 'hidden',
        padding: 6,
        zIndex: 1200,
    }),
    menuList: (base) => ({ ...base, padding: 0, maxHeight: 280 }),
    option: (base, state) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 12px',
        borderRadius: 4,
        fontSize: 13,
        fontWeight: state.isSelected ? 600 : 400,
        color: state.isSelected ? '#4338CA' : '#111827',
        backgroundColor: 'transparent',//state.isSelected ? '#EEF2FF' : state.isFocused ? '#F9FAFB' : 'transparent',
        cursor: 'pointer',
    }),
};

const Option = (props: OptionProps<MultiSelectOption, true>) => (
    <components.Option {...props}>
        <span>{props.data.label}</span>
        {props.isSelected && (
            <i className="bi bi-check" style={{ fontSize: 18, fontWeight: 700, color: '#4338CA' }} />
        )}
    </components.Option>
);

// const MultiValue = (_props: MultiValueProps<MultiSelectOption, true>) => null;
const MultiValue = (props: MultiValueProps<MultiSelectOption, true>) => {
    const { index, getValue, data, removeProps, selectProps } = props;
    const allSelected = Boolean((selectProps as { allSelected?: boolean }).allSelected);
    const allSelectedLabel = (selectProps as { allSelectedLabel?: string }).allSelectedLabel || 'All selected';
    const totalSelected = getValue().filter((item) => item.value !== SELECT_ALL_VALUE).length;

    const pillStyle: React.CSSProperties = {display: 'flex',alignItems: 'center',gap: 4,borderRadius: 4,padding: '3px 6px 3px 8px',fontSize: 12,fontWeight: 500,flexShrink: 0,marginRight: 4,};

    if (allSelected) {
        if (index > 0) return null;
        return (
            <div style={{ ...pillStyle, background: '#EEF2FF', color: '#4338CA' }}>
                <span style={{ whiteSpace: 'nowrap' }}>{allSelectedLabel}</span>
                <i className="bi bi-x" style={{ fontSize: 14, cursor: 'default', lineHeight: 1 }} onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        (removeProps as { onClick?: (event: React.MouseEvent) => void }).onClick?.(e);
                    }}
                />
            </div>
        );
    }

    if (data.value === SELECT_ALL_VALUE) return null;

    if (index < 2) {
        return (
            <div style={{...pillStyle, background: '#EEF2FF', color: '#4338CA', maxWidth: 120,}} >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {data.label}
                </span>
                <i className="bi bi-x" style={{ fontSize: 14, cursor: 'default', lineHeight: 1 }} onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        (removeProps as any).onClick?.(e);
                    }}
                />
            </div>
        );
    }

    if (index === 2 && totalSelected > 2) {
        return (
            <div style={{ ...pillStyle, background: '#F3F4F6', color: '#374151', fontWeight: 600, padding: '3px 8px', }}>
                +{totalSelected - 2}
            </div>
        );
    }
    return null;
};
const ValueContainer = ({ children, ...props }: ValueContainerProps<MultiSelectOption, true>) => {
    const selected = props.getValue();
    const label = selected.length === 0 ? null : selected.length === 1 ? selected[0].label : `${selected.length} selected`;

    return (
        <components.ValueContainer {...props}>
            {/* {!props.selectProps.inputValue && label && (
                <div style={{ position: 'absolute', left: 8, fontSize: 15, fontWeight: 500, color: '#111827', pointerEvents: 'none', }}>
                    {label}
                </div>
            )} */}
            {children}
        </components.ValueContainer>
    );
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({ options, value, onChange, placeholder = 'Select...', isSearchable = true, isDisabled = false, isClearable = true, name, enableSelectAll = false, selectAllLabel = 'Select All', allSelectedLabel = 'All selected' }) => {
    const allSelected = enableSelectAll && areAllSelected(options, value);
    const selectAllOption = React.useMemo<MultiSelectOption>(() => ({ value: SELECT_ALL_VALUE, label: selectAllLabel }), [selectAllLabel]);

    const sortedOptions = React.useMemo(() => {
        const selectedValues = new Set(value.map((v) => String(v.value)));
        const selected = options.filter(opt => selectedValues.has(String(opt.value)));
        const rest = options.filter(opt => !selectedValues.has(String(opt.value)));
        const sorted = [...selected, ...rest];
        return enableSelectAll && options.length > 0 ? [selectAllOption, ...sorted] : sorted;
    }, [options, value, enableSelectAll, selectAllOption]);

    const selectValue = allSelected ? [selectAllOption, ...value] : value;

    const handleChange = (selected: readonly MultiSelectOption[] | null) => {
        const next = selected ? [...selected] : [];
        if (!enableSelectAll) {
            onChange(next);
            return;
        }

        const withoutSelectAll = next.filter((option) => option.value !== SELECT_ALL_VALUE);
        const selectAllInNext = next.some((option) => option.value === SELECT_ALL_VALUE);
        const previouslyAllSelected = areAllSelected(options, value);

        if (selectAllInNext && !previouslyAllSelected) {
            onChange([...options]);
            return;
        }

        if (!selectAllInNext && previouslyAllSelected && withoutSelectAll.length === options.length) {
            onChange([]);
            return;
        }

        onChange(withoutSelectAll);
    };

    return (
        <Select<MultiSelectOption, true>
            isMulti
            options={sortedOptions}
            value={selectValue}
            onChange={handleChange}
            styles={customStyles}
            components={{ Option, MultiValue, ValueContainer }}
            placeholder={placeholder}
            isSearchable={isSearchable}
            isDisabled={isDisabled}
            isClearable={isClearable}
            name={name}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            filterOption={enableSelectAll
                ? (candidate, input) => candidate.value === SELECT_ALL_VALUE || optionFilter(candidate, input)
                : undefined}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            classNamePrefix="multi-select"
            {...({ allSelected, allSelectedLabel } as Record<string, unknown>)}
        />
    );
};

export default MultiSelectDropdown;